import { PortableText, type PortableTextComponents } from "@portabletext/react";
import { useState } from "react";
import cn from "mcn";

import type { NewFeature } from "../../__generated__/sanity/graphql-request";
import { assetRefToSanityUrl } from "./utils";

const modalBodyTextClass =
  "text-[length:var(--typography-text-small,14px)] font-normal leading-[20px] text-[var(--colour-text-default)]";

const modalAnnouncementComponents: PortableTextComponents = {
  block: {
    normal: ({ children }) => <p className={modalBodyTextClass}>{children}</p>,
    h1: ({ children }) => <h1 className={modalBodyTextClass}>{children}</h1>,
    h2: ({ children }) => <h2 className={modalBodyTextClass}>{children}</h2>,
  },
  marks: {
    strong: ({ children }) => <strong>{children}</strong>,
    link: ({ value, children }) => (
      <a href={value.href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ),
  },
  types: {
    breakline: () => <br />,
    // The modal is only opened once `getAnnouncementImageUrls` has been preloaded, so images are
    // already cached and can be rendered directly without a loading skeleton.
    image: ModalImage,
  },
};

const announcementComponents: PortableTextComponents = {
  block: {
    normal: ({ children }) => <p className="">{children}</p>,
    h1: ({ children }) => <h1 className="text-2xl font-bold">{children}</h1>,
    h2: ({ children }) => <h2 className="text-xl font-bold">{children}</h2>,
  },
  marks: {
    strong: ({ children }) => <strong>{children}</strong>,
    link: ({ value, children }) => (
      <a href={value.href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ),
  },
  types: {
    breakline: () => <br />,
    image: ImageWithSkeleton,
  },
};
export const AnnouncementBody = ({
  bodyRaw,
  variant = "default",
}: Pick<NewFeature, "bodyRaw"> & { variant?: "default" | "modal" }) => {
  return (
    <PortableText
      value={bodyRaw}
      components={variant === "modal" ? modalAnnouncementComponents : announcementComponents}
    />
  );
};

/**
 * Walks a PortableText `bodyRaw` tree and collects every referenced image's URL, so callers can
 * preload them ahead of rendering (e.g. to avoid the per-image skeleton popping in after the
 * text has already appeared).
 */
export function getAnnouncementImageUrls(bodyRaw: unknown): string[] {
  const refs = new Set<string>();

  const visit = (node: unknown) => {
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }
    if (node && typeof node === "object") {
      const obj = node as Record<string, unknown>;
      const asset = obj.asset as { _ref?: unknown } | undefined;
      if (obj._type === "image" && typeof asset?._ref === "string") {
        refs.add(asset._ref);
      }
      Object.values(obj).forEach(visit);
    }
  };

  visit(bodyRaw);
  return Array.from(refs).map(assetRefToSanityUrl);
}

type ImageWithSkeletonProps = {
  value: {
    asset: {
      _ref: string;
    };
  };
};

function ModalImage({ value }: ImageWithSkeletonProps) {
  const imageUrl = assetRefToSanityUrl(value.asset._ref);

  return (
    <div className="w-full max-w-[800px] my-4 mx-auto relative">
      <img src={imageUrl} alt="" className="max-w-full w-auto h-auto max-h-[800px] object-contain" />
    </div>
  );
}

function ImageWithSkeleton({ value }: ImageWithSkeletonProps) {
  const [loading, setLoading] = useState(true);
  const imageUrl = assetRefToSanityUrl(value.asset._ref);

  const handleImageLoad = () => {
    setLoading(false);
  };

  return (
    <div className="w-full max-w-[800px] my-4 mx-auto relative">
      {loading && <div className="absolute inset-0 bg-neutral-tonal animate-pulse rounded" />}
      <img
        src={imageUrl}
        alt=""
        onLoad={handleImageLoad}
        className={cn("max-w-full w-auto h-auto max-h-[800px] object-contain", [loading, "opacity-0", "opacity-100"])}
      />
    </div>
  );
}
